import React, { useState, useRef, useEffect } from 'react';

const CustomDropdown = ({ 
  options = [], 
  placeholder = 'Select an option...', 
  value, 
  onChange, 
  onOptionSelect,
  index,
  validationError
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options when search term changes
  useEffect(() => {
    if (!options || options.length === 0) {
      setFilteredOptions([]);
      return;
    }
    
    // If the search term is empty, show all options
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
      return;
    }
    
    // Filter options that include the search term (case insensitive)
    const filtered = options.filter(option => 
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  // Update search term when value changes
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    onChange(inputValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionClick = (option) => {
    onChange(option);
    setSearchTerm(option);
    onOptionSelect(option, index);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div 
      ref={dropdownRef} 
      style={{
        position: 'relative',
        width: '100%',
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '15px',
          fontSize: '16px',
          borderRadius: '8px',
          border: `1px solid ${validationError ? 'red' : '#ddd'}`,
          boxSizing: 'border-box',
        }}
      />
      
      {validationError && (
        <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
          {validationError}
        </div>
      )}
      
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '0 0 8px 8px',
            zIndex: 100,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, i) => (
              <div
                key={i}
                onClick={() => handleOptionClick(option)}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  borderBottom: i < filteredOptions.length - 1 ? '1px solid #eee' : 'none',
                  backgroundColor: searchTerm === option ? '#f5f5f5' : 'white',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 
                    searchTerm === option ? '#f5f5f5' : 'white';
                }}
              >
                {option}
              </div>
            ))
          ) : (
            <div style={{ padding: '10px 15px', color: '#999' }}>
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown; 